import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

type ToastType = 'success' | 'error' | 'info'

interface ToastOptions {
  type?: ToastType
  duration?: number
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void
}

interface Toast {
  id: string
  message: string
  type: ToastType
  duration: number
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timeouts = useRef<Map<string, number>>(new Map())

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))

    const timeoutId = timeouts.current.get(id)
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
      timeouts.current.delete(id)
    }
  }, [])

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`
      const type = options?.type ?? 'info'
      const duration = options?.duration ?? 4000

      setToasts((prev) => [...prev, { id, message, type, duration }])

      if (duration !== Infinity) {
        const timeoutId = window.setTimeout(() => {
          removeToast(id)
        }, duration)
        timeouts.current.set(id, timeoutId)
      }
    },
    [removeToast],
  )

  useEffect(() => {
    return () => {
      timeouts.current.forEach((timeoutId) => window.clearTimeout(timeoutId))
      timeouts.current.clear()
    }
  }, [])

  const value = useMemo(() => ({ showToast }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  return (
    <div className="fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-3">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast
  onDismiss: (id: string) => void
}) {
  const { id, message, type } = toast

  const iconMap: Record<ToastType, LucideIcon> = {
    success: CheckCircle2,
    error: AlertTriangle,
    info: Info,
  }

  const Icon = iconMap[type]

  const styles: Record<ToastType, string> = {
    success: 'border-green-200 bg-green-50 text-green-900',
    error: 'border-red-200 bg-red-50 text-red-900',
    info: 'border-slate-200 bg-white text-slate-900',
  }

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg shadow-black/5 ${styles[type]}`}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" />
      <p className="text-sm leading-5 whitespace-pre-line">{message}</p>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="ml-auto text-slate-500 transition hover:text-slate-700"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
